import React from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import classNames from "../../../utils/classNames";
import SearchPill from "../../atoms/SearchPill";
import Button from "../../atoms/Button";
import CloseButton from "../../atoms/CloseButton";
import StyleTagsEditor from "../StyleTagsEditor";
import AddressAutocompleteInput from "../AddressAutocompleteInput";
import AnchoredPortal from "../AnchoredPortal";
import FilterScrimBackdrop from "../FilterScrimBackdrop";
import FriendsFilterToggleButton from "../FriendsFilterToggleButton";
import {
    INSTRUMENT_TAG_OPTIONS,
    normalizeInstrumentOptions,
} from "../../../utils/instrumentOptions";
import { normalizeStyleTagList } from "../../../utils/styleTags";
import "./index.scss";

const FILTERS_PANEL_ID = "bands-catalog-filters-panel";

export default function BandCatalogToolbar({
    searchTerm,
    onSearchChange,
    genres = [],
    onGenresChange,
    location,
    onLocationChange,
    onLocationSelect,
    lookingForMembers = false,
    onLookingForMembersChange,
    memberRoles = [],
    onMemberRolesChange,
    onReset,
    canReset = false,
    onApply,
    hasAppliedFilters = false,
    filterTogglePortalTarget = null,
    renderFilterToggleInToolbar = true,
    filterToggleIconOnly = false,
    className = "",
}) {
    const { t } = useTranslation();
    const filtersTitleId = React.useId();
    const filtersAnchorRef = React.useRef(null);
    const genreEditorRef = React.useRef(null);
    const memberRolesEditorRef = React.useRef(null);
    const [isFiltersOpen, setIsFiltersOpen] = React.useState(false);
    const selectedGenres = normalizeStyleTagList(genres);
    const selectedMemberRoles = normalizeStyleTagList(memberRoles);
    const hasLocationFilter = Boolean(location?.trim());
    const activeSeekingFilterCount = lookingForMembers ? 1 + selectedMemberRoles.length : 0;
    const activeFilterCount = selectedGenres.length + (hasLocationFilter ? 1 : 0) + activeSeekingFilterCount;
    const hasActiveFilters = activeFilterCount > 0;
    const isFilterToggleActive = hasActiveFilters || hasAppliedFilters;

    const handleGenreSave = React.useCallback((nextGenres) => {
        onGenresChange(normalizeStyleTagList(nextGenres));
    }, [onGenresChange]);

    const handleMemberRolesSave = React.useCallback((nextRoles) => {
        onMemberRolesChange(normalizeStyleTagList(nextRoles));
    }, [onMemberRolesChange]);

    const handleLookingForMembersChange = React.useCallback((event) => {
        const nextValue = event.target.checked;
        onLookingForMembersChange(nextValue);
        if (!nextValue) {
            onMemberRolesChange([]);
        }
    }, [onLookingForMembersChange, onMemberRolesChange]);

    const handleToggleFilters = React.useCallback(() => {
        setIsFiltersOpen((currentValue) => !currentValue);
    }, []);

    const handleCloseFilters = React.useCallback(() => {
        setIsFiltersOpen(false);
    }, []);

    const handleResetClick = React.useCallback(() => {
        onReset();
        handleCloseFilters();
    }, [handleCloseFilters, onReset]);

    const handleApplyClick = React.useCallback(() => {
        const flushDraftGenres = genreEditorRef.current?.flushDraft?.() ?? Promise.resolve(selectedGenres);
        const flushDraftMemberRoles = lookingForMembers
            ? (memberRolesEditorRef.current?.flushDraft?.() ?? Promise.resolve(selectedMemberRoles))
            : Promise.resolve([]);

        Promise.all([flushDraftGenres, flushDraftMemberRoles])
            .then(([nextGenres, nextMemberRoles]) => {
                onApply({
                    genres: Array.isArray(nextGenres)
                        ? normalizeStyleTagList(nextGenres)
                        : selectedGenres,
                    lookingForMembers,
                    memberRoles: lookingForMembers && Array.isArray(nextMemberRoles)
                        ? normalizeStyleTagList(nextMemberRoles)
                        : [],
                });
                handleCloseFilters();
            });
    }, [handleCloseFilters, lookingForMembers, onApply, selectedGenres, selectedMemberRoles]);

    const filterToggle = (
        <div
            className={classNames(
                "band-catalog-toolbar__filters-anchor",
                isFiltersOpen && "band-catalog-toolbar__filters-anchor--open",
            )}
            ref={filtersAnchorRef}
        >
            <FriendsFilterToggleButton
                label={t("bandsWelcomePage.toolbar.filterToggle")}
                expanded={isFiltersOpen}
                active={isFilterToggleActive}
                controlsId={FILTERS_PANEL_ID}
                onClick={handleToggleFilters}
                iconOnly={filterToggleIconOnly}
                className="band-catalog-toolbar__filter-toggle"
            />
        </div>
    );

    return (
        <div className={classNames("band-catalog-toolbar", className)} role="search">
            <FilterScrimBackdrop
                open={isFiltersOpen}
                anchorRef={filtersAnchorRef}
                onClose={handleCloseFilters}
                className="band-catalog-toolbar__filters-backdrop"
                floatingClassName="band-catalog-toolbar__filter-toggle-floating"
            >
                <FriendsFilterToggleButton
                    label={t("bandsWelcomePage.toolbar.filterToggle")}
                    expanded={isFiltersOpen}
                    active={isFilterToggleActive}
                    controlsId={FILTERS_PANEL_ID}
                    onClick={handleToggleFilters}
                    iconOnly={filterToggleIconOnly}
                    className="band-catalog-toolbar__filter-toggle"
                />
            </FilterScrimBackdrop>

            <SearchPill
                className="band-catalog-toolbar__search"
                value={searchTerm}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={t("bandsWelcomePage.toolbar.searchPlaceholder")}
                ariaLabel={t("bandsWelcomePage.toolbar.searchAria")}
                onClear={() => onSearchChange("")}
                clearAriaLabel={t("bandsWelcomePage.toolbar.searchClearAria")}
            />

            {filterTogglePortalTarget
                ? createPortal(filterToggle, filterTogglePortalTarget)
                : (renderFilterToggleInToolbar ? filterToggle : null)}

            <AnimatePresence initial={false}>
                {isFiltersOpen ? (
                    <AnchoredPortal
                        id={FILTERS_PANEL_ID}
                        className="band-catalog-toolbar__filters-portal"
                        anchorRef={filtersAnchorRef}
                        placement="bottom-end"
                        offset={{ x: 0, y: 10 }}
                        gap={12}
                        onClose={handleCloseFilters}
                    >
                        <motion.div
                            key="bands-catalog-filters"
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.98 }}
                            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                        >
                            <section
                                className="band-catalog-toolbar__filters-panel"
                                role="dialog"
                                aria-modal="false"
                                aria-labelledby={filtersTitleId}
                            >
                                <div className="band-catalog-toolbar__filters-header">
                                    <div className="band-catalog-toolbar__filters-heading">
                                        <h3 id={filtersTitleId} className="band-catalog-toolbar__filters-title">
                                            {t("bandsWelcomePage.toolbar.filtersTitle")}
                                        </h3>
                                        <span className="band-catalog-toolbar__filters-badge">
                                            {activeFilterCount}
                                        </span>
                                    </div>
                                    <CloseButton
                                        className="band-catalog-toolbar__filters-close"
                                        ariaLabel={t("actions.close")}
                                        onClick={handleCloseFilters}
                                    />
                                </div>

                                <div className="band-catalog-toolbar__filters-body">
                                    <div className="band-catalog-toolbar__filter-row">
                                        <label className="band-catalog-toolbar__filter-label">
                                            {t("bandsWelcomePage.toolbar.genreLabel")}
                                        </label>
                                        <StyleTagsEditor
                                            ref={genreEditorRef}
                                            values={selectedGenres}
                                            onSave={handleGenreSave}
                                            placeholder={selectedGenres.length ? "" : t("bandsWelcomePage.toolbar.genreTriggerPlaceholder")}
                                            ariaLabel={t("bandsWelcomePage.toolbar.genreAria")}
                                            removeLabel={t("bandsWelcomePage.toolbar.genreClearAria")}
                                            removeLabelToken="style"
                                            customOptionLabel={t("bandsWelcomePage.toolbar.genreCustomOption")}
                                            emptyLabel={t("bandsWelcomePage.toolbar.genreNoResults")}
                                            className="band-catalog-toolbar__genre-picker"
                                            maxLength={100}
                                            menuAnchor="control"
                                        />
                                    </div>

                                    <div className="band-catalog-toolbar__filter-row band-catalog-toolbar__filter-row--seeking">
                                        <span className="band-catalog-toolbar__filter-label">
                                            {t("bandsWelcomePage.toolbar.seekingLabel")}
                                        </span>
                                        <label className="band-catalog-toolbar__seeking-toggle">
                                            <input
                                                type="checkbox"
                                                className="band-catalog-toolbar__seeking-checkbox"
                                                checked={lookingForMembers}
                                                onChange={handleLookingForMembersChange}
                                            />
                                            <span className="band-catalog-toolbar__seeking-checkbox-box" aria-hidden="true">
                                                <svg viewBox="0 0 24 24" focusable="false">
                                                    <path d="M20 6 9 17l-5-5" />
                                                </svg>
                                            </span>
                                            <span className="band-catalog-toolbar__seeking-toggle-label">
                                                {t("bandsWelcomePage.toolbar.lookingForMembers")}
                                            </span>
                                        </label>
                                        {lookingForMembers ? (
                                            <div className="band-catalog-toolbar__member-roles">
                                                <span className="band-catalog-toolbar__member-roles-label">
                                                    {t("bandsWelcomePage.toolbar.memberRolesLabel")}
                                                </span>
                                                <StyleTagsEditor
                                                    ref={memberRolesEditorRef}
                                                    values={selectedMemberRoles}
                                                    onSave={handleMemberRolesSave}
                                                    options={INSTRUMENT_TAG_OPTIONS}
                                                    loadOptions={null}
                                                    normalizeOptions={normalizeInstrumentOptions}
                                                    placeholder={selectedMemberRoles.length ? "" : t("bandsWelcomePage.toolbar.memberRolesPlaceholder")}
                                                    ariaLabel={t("bandsWelcomePage.toolbar.memberRolesAria")}
                                                    removeLabel={t("bandsWelcomePage.toolbar.memberRolesRemove")}
                                                    removeLabelToken="role"
                                                    customOptionLabel={t("bandsWelcomePage.toolbar.memberRolesCustomOption")}
                                                    emptyLabel={t("bandsWelcomePage.toolbar.memberRolesNoResults")}
                                                    className="band-catalog-toolbar__member-role-picker"
                                                    maxLength={100}
                                                    menuAnchor="control"
                                                />
                                            </div>
                                        ) : null}
                                    </div>

                                    <div className="band-catalog-toolbar__filter-row">
                                        <AddressAutocompleteInput
                                            id="bands-catalog-location-filter"
                                            label={t("bandsWelcomePage.toolbar.locationLabel")}
                                            ariaLabel={t("bandsWelcomePage.toolbar.locationAria")}
                                            className="band-catalog-toolbar__location-picker"
                                            value={location}
                                            onChange={onLocationChange}
                                            onSelect={onLocationSelect}
                                            placeholder={t("bandsWelcomePage.toolbar.locationPlaceholder")}
                                            countryCode="US"
                                            minChars={3}
                                            debounceMs={300}
                                            loadingText={t("bandsWelcomePage.toolbar.locationLoading")}
                                            emptyText={t("bandsWelcomePage.toolbar.locationEmpty")}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>

                                <div className="band-catalog-toolbar__filters-footer">
                                    <Button
                                        variant="secondary"
                                        shape="pill"
                                        size="medium"
                                        className="band-catalog-toolbar__reset"
                                        onClick={handleResetClick}
                                        disabled={!canReset}
                                    >
                                        {t("bandsWelcomePage.toolbar.reset")}
                                    </Button>
                                    <Button
                                        type="button"
                                        shape="pill"
                                        size="medium"
                                        onClick={handleApplyClick}
                                        className="band-catalog-toolbar__apply"
                                    >
                                        {t("bandsWelcomePage.toolbar.apply")}
                                    </Button>
                                </div>
                            </section>
                        </motion.div>
                    </AnchoredPortal>
                ) : null}
            </AnimatePresence>
        </div>
    );
}

BandCatalogToolbar.propTypes = {
    searchTerm: PropTypes.string.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    genres: PropTypes.arrayOf(PropTypes.string),
    onGenresChange: PropTypes.func.isRequired,
    location: PropTypes.string.isRequired,
    onLocationChange: PropTypes.func.isRequired,
    onLocationSelect: PropTypes.func.isRequired,
    lookingForMembers: PropTypes.bool,
    onLookingForMembersChange: PropTypes.func.isRequired,
    memberRoles: PropTypes.arrayOf(PropTypes.string),
    onMemberRolesChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    canReset: PropTypes.bool,
    onApply: PropTypes.func.isRequired,
    hasAppliedFilters: PropTypes.bool,
    filterTogglePortalTarget: PropTypes.object,
    renderFilterToggleInToolbar: PropTypes.bool,
    filterToggleIconOnly: PropTypes.bool,
    className: PropTypes.string,
};
